import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Smartphone } from "lucide-react";
import { TemplatePreview } from "@/components/templates/TemplatePreview";
import { Select } from "@/components/Select";
import { Campaign, Recipient } from "@/services/campaigns.service";
import { Template } from "@/services/templates.service";
import { whatsappAccountService } from "@/services/whatsapp-account.service";

export function CampaignMessagePreview({
  campaign,
  template,
  recipients,
  loading,
}: {
  campaign: Campaign;
  template: Template | null;
  recipients: Recipient[];
  loading: boolean;
}) {
  const [recipientUid, setRecipientUid] = useState("");
  const [sender, setSender] = useState<{ name: string; phone?: string }>({
    name: "Your business",
  });
  useEffect(() => {
    let cancelled = false;
    setSender({ name: "Your business" });
    whatsappAccountService
      .getById(campaign.whatsappAccountUid)
      .then((account) => {
        if (!cancelled)
          setSender({
            name: account.businessName || "Your business",
            phone: account.displayPhoneNumber,
          });
      })
      .catch(() => {
        /* A disconnected account must not hide the message history. */
      });
    return () => {
      cancelled = true;
    };
  }, [campaign.whatsappAccountUid]);
  const recipient =
    recipients.find((row) => row.recipientUid === recipientUid) ??
    recipients[0];
  const snapshot = recipient?.messageSnapshot;
  const message = snapshot ?? template;
  const values = recipient
    ? recipient.variables
    : (campaign.templateVariableOverrides ?? {});
  const text = (value?: string, map?: Record<string, string>) =>
    (value || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (match, key: string) =>
      map?.[key] ? `{{${map[key]}}}` : match,
    );
  const media = snapshot ? undefined : campaign.headerOverride;
  return (
    <aside className="min-w-0 self-start rounded-2xl border border-border bg-surface-alt/50 p-5 xl:sticky xl:top-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold text-text-primary">
          <Smartphone size={18} className="text-primary" />
          Campaign message
        </h2>
        <span className="rounded-full border border-border bg-surface px-2 py-1 text-[10px] font-medium text-text-muted">
          {snapshot ? "Saved at send" : "Preview"}
        </span>
      </div>
      <div className="mb-5 rounded-xl border border-border bg-surface p-3">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          <FileText size={13} />
          Template · {message?.language || campaign.language}
        </div>
        <Link
          to={`/templates/${campaign.templateUid}`}
          className="mt-1 flex items-start justify-between gap-2 break-all text-sm font-semibold text-text-primary hover:text-primary"
        >
          {message?.name || campaign.templateUid}
          <ArrowUpRight size={15} className="mt-0.5 shrink-0" />
        </Link>
        <p className="mt-2 text-xs text-text-muted">
          From {sender.name}
          {sender.phone ? ` · ${sender.phone}` : ""}
        </p>
      </div>
      {recipients.length > 0 && (
        <div className="mb-4">
          <label
            htmlFor="campaign-preview-recipient"
            className="mb-1.5 block text-xs font-medium text-text-secondary"
          >
            Preview recipient · current page
          </label>
          <Select
            id="campaign-preview-recipient"
            value={recipient?.recipientUid || ""}
            onChange={(e) => setRecipientUid(e.target.value)}
          >
            {recipients.map((row) => (
              <option key={row.recipientUid} value={row.recipientUid}>
                {row.phone} · {row.status.toLowerCase()}
              </option>
            ))}
          </Select>
        </div>
      )}
      {loading ? (
        <div className="skeleton mx-auto h-[480px] max-w-[300px] rounded-[38px]" />
      ) : message ? (
        <TemplatePreview
          headerType={message.type === "CAROUSEL" ? "NONE" : message.headerType}
          headerText={text(message.headerText, message.headerVariableMap)}
          headerImageUrl={media?.imageUrl ?? message.headerImageUrl}
          headerVideoUrl={media?.videoUrl ?? message.headerVideoUrl}
          headerDocumentUrl={media?.documentUrl ?? message.headerDocumentUrl}
          headerDocumentFilename={
            media?.documentFilename ?? message.headerDocumentFilename
          }
          headerLocation={media?.location ?? message.headerLocation}
          body={text(message.body, message.variableMap)}
          footer={message.type === "CAROUSEL" ? undefined : message.footer}
          buttons={message.type === "CAROUSEL" ? [] : (message.buttons ?? [])}
          cards={message.type === "CAROUSEL" ? message.cards : undefined}
          samples={values}
          contactName={sender.name}
        />
      ) : (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-text-muted">
          This template is no longer available. Recipient delivery records are
          still shown.
        </div>
      )}
      <p className="mt-5 text-center text-xs leading-relaxed text-text-muted">
        {snapshot
          ? `Content saved for this recipient’s send attempt. Status: ${recipient?.status.toLowerCase()}.`
          : campaign.startedAt
            ? "Reconstructed from the current template and saved recipient values. Older sends do not have a content snapshot."
            : "Campaign preview. Contact variables are filled when recipients are prepared."}
      </p>
    </aside>
  );
}
